import React, { useEffect } from 'react';
import { useTutorialContext } from '../../contexts/TutorialContext';
import { TutorialDefinition } from '../../types/tutorial';
import TutorialOverlay from './TutorialOverlay';

interface TutorialManagerProps {
  tutorials: TutorialDefinition[];
}

const TutorialManager: React.FC<TutorialManagerProps> = ({ tutorials }) => {
  const {
    state,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
    dismissTutorial,
  } = useTutorialContext();

  const currentTutorial = tutorials.find((t) => t.id === state.currentTutorial);
  const currentStep = currentTutorial?.steps[state.currentStep];

  // Skip steps where target element doesn't exist
  useEffect(() => {
    if (!currentTutorial || !currentStep) return;

    // If step has a target selector, check if element exists
    if (currentStep.targetSelector) {
      const targetElement = document.querySelector(currentStep.targetSelector);
      if (!targetElement) {
        // Target doesn't exist, skip to next step
        if (state.currentStep < currentTutorial.steps.length - 1) {
          nextStep();
        } else {
          completeTutorial(currentTutorial.id);
        }
        return;
      }
    }

    // Call step action if provided
    if (currentStep.action) {
      currentStep.action();
    }
  }, [
    currentTutorial,
    currentStep,
    completeTutorial,
    nextStep,
    state.currentStep,
  ]);

  if (!currentTutorial) return null;
  if (!currentStep) return null;

  const handleNext = () => {
    if (state.currentStep < currentTutorial.steps.length - 1) {
      nextStep();
    } else {
      completeTutorial(currentTutorial.id);
    }
  };

  const handleDismiss = () => {
    dismissTutorial(currentTutorial.id);
  };

  return (
    <TutorialOverlay
      step={currentStep}
      totalSteps={currentTutorial.steps.length}
      currentStepIndex={state.currentStep}
      initialAnimation={currentTutorial.initialAnimation}
      onNext={handleNext}
      onPrevious={previousStep}
      onSkip={skipTutorial}
      onDismiss={handleDismiss}
    />
  );
};

export default TutorialManager;
